import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "../../../../components/ui/Button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedFixture, setStep } from "../../../../redux/Slices/matchSchedulerSlice";
import { usePopularLeagues, useSearchLeagues, useUpcomingFixtures, useLiveFixtures, useLeagueSeasons } from "../../../../queries/matchScheduler";

export default function FixtureBrowser() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selectedFixture = useSelector((s) => s.matchScheduler.selectedFixture);

    const STATUS_BADGE = {
        NS:  { label: t('match_scheduler.upcoming'),  cls: "bg-blue-100 text-blue-700"  },
        "1H":{ label: t('match_scheduler.first_half'), cls: "bg-green-100 text-green-700"},
        HT:  { label: t('match_scheduler.half_time'),  cls: "bg-yellow-100 text-yellow-700"},
        "2H":{ label: t('match_scheduler.second_half'),cls: "bg-green-100 text-green-700"},
        FT:  { label: t('match_scheduler.full_time'),  cls: "bg-gray-100 text-gray-600"  },
    };

    const [tab,           setTab]           = useState("upcoming"); // "upcoming" | "live"
    const [leagueSearch,  setLeagueSearch]  = useState("");
    const [selectedLeague, setSelectedLeague] = useState(null);
    // null until the league's season list resolves. It used to default to
    // new Date().getFullYear(), which was never in the hardcoded options array
    // — so the dropdown rendered blank and nudged users onto a stale season.
    const [season,        setSeason]        = useState(null);
    const [fetchEnabled,  setFetchEnabled]  = useState(false);

    const { data: popularLeagues,  isLoading: loadingPopular }  = usePopularLeagues();
    const { data: searchedLeagues, isLoading: loadingSearch  }  = useSearchLeagues(leagueSearch.length >= 2 ? leagueSearch : "");
    const { data: seasonInfo } = useLeagueSeasons(selectedLeague?.id);
    const { data: upcoming, isLoading: loadingUpcoming, isFetched: fixturesFetched, refetch: refetchUpcoming } = useUpcomingFixtures(
        selectedLeague?.id, season, fetchEnabled
    );
    const { data: live, isLoading: loadingLive } = useLiveFixtures();

    // Preselect the vendor's current season whenever the league changes. The
    // league object carries `season` too (both popular + search populate it),
    // so the dropdown is usable before /seasons lands.
    useEffect(() => {
        const current = seasonInfo?.current ?? selectedLeague?.season ?? null;
        if (current) setSeason(current);
    }, [seasonInfo?.current, selectedLeague?.id, selectedLeague?.season]);

    const seasonOptions = (seasonInfo?.seasons?.length
        ? seasonInfo.seasons
        : [selectedLeague?.season].filter(Boolean)
    ).map((y) => ({ label: String(y), value: y }));

    // Array.isArray, not `?? []` — `??` only catches null/undefined, so a
    // wrapped {data:[...]} sailed straight through. The two consumers below
    // then failed in opposite ways: `leagueOptions.length > 0` silently
    // rendered nothing (dead league picker) while `fixtures.map()` threw and
    // white-paged the Live Now tab. The hooks unwrap now; this keeps a future
    // shape change to an empty picker instead of a crash.
    const asRows = (x) => (Array.isArray(x) ? x : []);

    const leagueOptions = leagueSearch.length >= 2
        ? asRows(searchedLeagues)
        : asRows(popularLeagues);

    const fixtures = asRows(tab === "live" ? live : upcoming);

    const isLoading = tab === "live" ? loadingLive : loadingUpcoming;

    const handleSelectFixture = (fixture) => {
        dispatch(setSelectedFixture(fixture));
        dispatch(setStep(2));
    };

    return (
        <div>
            {/* Tab selector */}
            <div className="flex gap-2 mb-4">
                <Button
                    label={t('match_scheduler.upcoming')}
                    icon="pi pi-calendar"
                    className={`p-button-sm ${tab === "upcoming" ? "p-button-primary" : "p-button-outlined"}`}
                    onClick={() => setTab("upcoming")}
                />
                <Button
                    label={t('match_scheduler.live_now_btn')}
                    icon="pi pi-circle-fill"
                    className={`p-button-sm ${tab === "live" ? "p-button-danger" : "p-button-outlined"}`}
                    onClick={() => setTab("live")}
                />
            </div>

            {/* League + season picker (upcoming only) */}
            {tab === "upcoming" && (
                <div className="flex gap-3 mb-4 flex-wrap items-end">
                    <div className="flex-1 min-w-40">
                        <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.league')}</label>
                        <div className="flex gap-2">
                            <InputText
                                value={leagueSearch}
                                onChange={(e) => { setLeagueSearch(e.target.value); setSelectedLeague(null); }}
                                placeholder={t('match_scheduler.search_league')}
                                className="text-sm flex-1"
                            />
                        </div>
                        {leagueOptions.length > 0 && (
                            <div className="mt-1 border rounded shadow-sm bg-white max-h-40 overflow-y-auto z-50 relative">
                                {leagueOptions.map((lg) => (
                                    <div
                                        key={lg.id}
                                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${
                                            selectedLeague?.id === lg.id ? "bg-blue-100" : ""
                                        }`}
                                        onClick={() => { setSelectedLeague(lg); setLeagueSearch(lg.name); }}
                                    >
                                        {lg.logo && <img src={lg.logo} alt="" className="w-5 h-5 object-contain" />}
                                        <span>{lg.name}</span>
                                        <span className="text-gray-400 text-xs ml-auto">{lg.country}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.season')}</label>
                        <Dropdown
                            value={season}
                            options={seasonOptions}
                            onChange={(e) => setSeason(e.value)}
                            disabled={!selectedLeague || seasonOptions.length === 0}
                            placeholder={t('match_scheduler.season')}
                            className="text-sm"
                        />
                    </div>
                    <Button
                        label={t('match_scheduler.load_fixtures')}
                        icon="pi pi-refresh"
                        className="p-button-sm p-button-outlined"
                        disabled={!selectedLeague || !season}
                        onClick={() => { setFetchEnabled(true); refetchUpcoming(); }}
                    />
                </div>
            )}

            {/* Fixtures list */}
            {isLoading ? (
                <div className="flex justify-center py-10"><ProgressSpinner /></div>
            ) : fixtures.length === 0 ? (
                <p className="text-center text-gray-400 py-10 text-sm">
                    {tab === "live"
                        ? t('match_scheduler.no_live')
                        : fixturesFetched
                            /* Loaded and genuinely empty — distinct from "you
                               haven't pressed Load yet". A wrong league id or a
                               finished season returns 200 with zero rows, so
                               without this split both read as "pick a league". */
                            ? t('match_scheduler.no_fixtures_for_season')
                            : t('match_scheduler.select_league_load')}
                </p>
            ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {fixtures.map((f) => {
                        const badge   = STATUS_BADGE[f.status] ?? { label: f.status, cls: "bg-gray-100 text-gray-600" };
                        const isSelected = selectedFixture?.fixture_id === f.fixture_id;
                        return (
                            <div
                                key={f.fixture_id}
                                onClick={() => handleSelectFixture(f)}
                                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                    isSelected
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        {f.league_logo && <img src={f.league_logo} alt="" className="w-4 h-4 object-contain" />}
                                        <span>{f.league_name}</span>
                                    </div>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                                        {badge.label}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1">
                                        {f.home_logo && <img src={f.home_logo} alt="" className="w-6 h-6 object-contain" />}
                                        <span className="font-medium text-sm text-gray-800">{f.home_team}</span>
                                    </div>
                                    <div className="text-center px-3">
                                        {f.status === "NS" ? (
                                            <span className="text-xs text-gray-500">
                                                {new Date(f.kickoff_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                                            </span>
                                        ) : (
                                            <span className="font-bold text-gray-700">
                                                {f.score.home ?? 0} – {f.score.away ?? 0}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-1 justify-end">
                                        <span className="font-medium text-sm text-gray-800">{f.away_team}</span>
                                        {f.away_logo && <img src={f.away_logo} alt="" className="w-6 h-6 object-contain" />}
                                    </div>
                                </div>
                                {f.venue && (
                                    <p className="text-xs text-gray-400 mt-1 text-center">{f.venue}</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
